<template>
	<section class="b360-grid-row preference-center-section">
		<modal
			v-if="showDeleteModal"
			modalTitle="Delete Address"
			actionBtnName="Delete"
			cancelBtnName="Cancel"
			class="md delete-address-modal"
			@clicked="handleDeleteAddress"
			@close="(showDeleteModal = false), (addressToDelete = null)"
			:waiting="addressIsBeingDeleted"
		>
			<div slot="modalContent" class="modal-content-delete">
				<h2>Are you sure you want to remove this address?</h2>
				<p>This address will be deleted immediately form your user profile.</p>
			</div>
		</modal>
		<TitleDescription
			class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 title-description--span3"
			title="Your Addresses"
			description="Add multiple addresses to your Mortgage Matchup profile. Your primary address is the default and should be the same as your mailing address. Only one address is allowed per state."
		/>
		<section class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8">
			<Accordion
				v-for="(address, index) in addresses"
				:key="address.localId"
				:name="`${address.displayOrder}`"
				:accordionLabel="addressAccordionHeaders[index]"
				:isPrimaryAddress="address.displayOrder === 0"
				:newAddress="newAddress ? index === newAddress : false"
				ref="addressAccordion"
			>
				<MailingAddress
					slot="accordionContent"
					:addressIndex="index"
					:addressDisplayOrder="address.displayOrder"
					:address="address"
					@updateAddress="updateAddress"
				/>
				<div
					class="b360-col-xs-12 b360-col-sm-12 b360-col-md-12 address-actions"
					slot="accordionContent"
				>
					<TextButton
						v-if="
							addressesCanBeDeleted &&
							((address.displayOrder === 0 && !newAddress) ||
								address.displayOrder !== 0)
						"
						aria-label="Remove Address"
						@onClick="openDeleteAddressModal(address)"
						data-test="remove-address"
					>
						<font-awesome-icon :icon="['fal', 'times']" class="remove-icon" />
						Remove
					</TextButton>
					<div class="divider" v-if="address.displayOrder !== 0">|</div>
					<TextButton
						v-if="address.displayOrder !== 0"
						@onClick="setAsPrimaryAddress(address.displayOrder)"
						aria-label="Set as Primary Address"
						data-test="set-primary-address"
					>
						Set as Primary
					</TextButton>
				</div>
			</Accordion>
			<div
				v-if="!isEligibleToAddAddress && canAddAddress"
				class="disabled-additional-address-info"
				data-test="disabled-additional-address-info"
			>
				In order to add new addresses, the following fields must been filled out
				- <br />profile photo, phone number, email, & website.
			</div>
			<BaseButton
				v-if="canAddAddress"
				:color="isEligibleToAddAddress ? 'blue' : 'gray'"
				width="auto"
				aria-label="Add an additional address"
				:disabled="disableAddButton"
				@onClick="addAddress"
				data-test="add-address"
				class="add-address-button"
			>
				<font-awesome-icon :icon="['far', 'plus']" class="plus-icon" />
				Add Address
			</BaseButton>
		</section>
	</section>
</template>

<script>
import Accordion from '../global/Accordion.vue';
import BaseButton from '../global/BaseButton.vue';
import TextButton from '../global/TextButton.vue';
import MailingAddress from './MailingAddress.vue';
import TitleDescription from '@/components/global/TitleDescription.vue';
import Modal from '@/components/global/Modal.vue';
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
	name: 'MailingAddressesLayout',
	components: {
		Accordion,
		BaseButton,
		MailingAddress,
		Modal,
		TextButton,
		TitleDescription,
	},
	data() {
		return {
			showDeleteModal: false,
			addressIsBeingDeleted: false,
			addressToDelete: null,
			addresses: [],
			newAddress: null,
			addressAccordionHeaders: [],
			profileHasBeenSaved: this.profileSaved,
			nextId: 0,
		};
	},
	props: {
		profileAddresses: Array,
		profileSaved: Boolean,
	},
	watch: {
		addresses(newVal) {
			//remove the localId from objects before updating
			let addressesToSend = JSON.parse(JSON.stringify(newVal));
			addressesToSend.forEach((address) => {
				delete address.localId;
			});
			this.$emit('update:profileAddresses', addressesToSend);
		},
		profileSaved(newVal) {
			//when the profile is saved, we want to make sure we update the addressAccordionHeaders so that it is accurate and the saved address(es) no longer show a "Add New Address" header
			this.profileHasBeenSaved = newVal;
			if (this.profileHasBeenSaved) {
				this.setAddressAccordionHeaders();
				this.newAddress = null;
			}
		},
	},
	computed: {
		...mapGetters(['roleIsNotActive']),
		...mapState({
			userId: (state) => state.profile.userId,
			contactId: (state) => state.profile.contactId,
			userProfileImage: (state) => state.profile.profilePhoto,
			userProfile: (state) => state.profile,
		}),
		canAddAddress() {
			//atm we are only allowing for a total of three addresses
			if (this.addresses) {
				return this.addresses.length < 3;
			}
		},
		isEligibleToAddAddress() {
			//for user to be eligible to add multiple addresses, they need to have a user profile photo, email address, website url, and phone number.
			if (this.userProfileImage === null) {
				return false;
			} else {
				return (
					this.userProfileImage.imageUri !== '' &&
					this.userProfile.websiteUrl !== null &&
					this.userProfile.websiteUrl !== '' &&
					this.userProfile.phoneNumber !== null &&
					this.userProfile.phoneNumber !== '' &&
					this.userProfile.email !== '' &&
					this.userProfile.email !== null
				);
			}
		},
		addressesLength() {
			return this.addresses.length;
		},
		addressesCanBeDeleted() {
			//as long as we have one address, we can delete the others
			return this.addressesLength > 1;
		},
		addressesInOrder() {
			if (this.addresses) {
				return this.addresses.sort((a, b) => a.displayOrder - b.displayOrder);
			}
		},
		disableAddButton() {
			return (
				this.roleIsNotActive('EditAPersonalAddressLine1') ||
				!this.isEligibleToAddAddress
			);
		},
	},
	methods: {
		...mapActions([
			'removeAdditionalAddress',
			'deleteAddress',
			'setAddressOrder',
		]),
		updateAddress(payload) {
			// Replace the old address with the updated one, keeping the existing localId
			this.addresses.splice(payload.index, 1, {
				...payload.value,
				localId: this.addresses[payload.index].localId,
			});
		},
		addAddress() {
			let additionalAddress = {
				addressLine1: '',
				addressLine2: '',
				city: '',
				state: '',
				zip: '',
				displayOrder: this.addressesLength,
				localId: this.nextId++,
			};
			this.addresses.push(additionalAddress);
			this.addressAccordionHeaders.push('Add New Address');
			let newAddressIndex = this.addressesLength - 1;
			//new address will house the index so that we can let the accordion know that it needs to also have the blue borders
			this.newAddress = newAddressIndex;
			//this will target and click the accordion to open it for new address so that it is open
			this.$nextTick(() => {
				this.$refs.addressAccordion[
					newAddressIndex
				].$refs.accordionInputRef.click();
			});
		},
		openDeleteAddressModal(address) {
			this.addressToDelete = address;
			let index = address.displayOrder;
			if (this.addressAccordionHeaders[index] === 'Add New Address') {
				//if the address is empty and new we just remove it and don't make the call.
				this.addresses = this.addresses.filter(
					(address) =>
						address.displayOrder !== this.addressToDelete.displayOrder,
				);
				this.addressAccordionHeaders.splice(address.displayOrder, 1);
				this.setAddressesDisplayOrder();
			} else {
				this.showDeleteModal = true;
			}
		},
		async handleDeleteAddress() {
			const payload = {
				UserId: this.userId,
				ContactId: this.contactId,
				DisplayOrder: this.addressToDelete.displayOrder,
				UpdateByUserId: this.userId,
			};
			this.addressIsBeingDeleted = true;
			await this.deleteAddress(payload);
			//remove header for deleted address
			this.addressAccordionHeaders.splice(this.addressToDelete.displayOrder, 1);
			this.addresses.splice(this.addressToDelete.displayOrder, 1);
			this.removeAdditionalAddress(this.addressToDelete.displayOrder);
			this.setAddressOrder();
			this.addressToDelete = null;
			this.addressIsBeingDeleted = false;
			this.showDeleteModal = false;
			this.setAddressesDisplayOrder();
		},
		setAsPrimaryAddress(displayOrder) {
			const addressOfNewPrimary = this.addresses.filter(
				(address) => address.displayOrder === displayOrder,
			);
			this.addresses.forEach((address) => {
				if (address.displayOrder === 0) {
					address.displayOrder = displayOrder;
				}
				if (address.addressLine1 === addressOfNewPrimary[0].addressLine1) {
					address.displayOrder = 0;
				}
			});
			//need to reorder the addresses headers
			this.setAddressAccordionHeaders();
		},
		setAddressesDisplayOrder() {
			let i = 0;
			this.addresses.sort((a, b) => a.displayOrder - b.displayOrder);
			for (let address of this.addresses) {
				address.displayOrder = i;
				i++;
			}
		},
		setAddressAccordionHeaders() {
			let newAddressAccordionHeaders = [];
			//sort addresses by display number then remake the header array
			this.addresses
				.sort((a, b) => a.displayOrder - b.displayOrder)
				.forEach((address) => {
					let header = `${address.addressLine1}, ${address.state} ${address.zip}`;
					if (header !== '') {
						newAddressAccordionHeaders.push(header);
					} else {
						newAddressAccordionHeaders.push('Add New Address');
					}
				});
			this.addressAccordionHeaders = newAddressAccordionHeaders;
		},
	},
	mounted() {
		// this.addresses = this.profileAddresses;
		//add in localId to objects so we have unique key to work with
		this.addresses = this.profileAddresses.map((address, index) => {
			return { ...address, localId: index };
		});
		this.nextId = this.addresses.length;
		//get the header text for existing addresses
		this.setAddressAccordionHeaders();
	},
};
</script>

<style lang="scss">
.remove-icon {
	margin-right: 0.5rem;
}
.divider {
	display: inline-block;
	padding-left: 1rem;
	padding-right: 1rem;
	color: $brand-blue;
}
.address-actions {
	padding-bottom: 1rem;
}
.disabled-additional-address-info {
	font-size: smaller;
	font-style: italic;
	line-height: 1rem;
	margin-bottom: 1rem;
}
.plus-icon {
	padding-right: 0.5rem;
}
.add-address-button {
	padding-left: 1rem;
	padding-right: 1rem;
}
.b360-grid-row preference-center-section {
	padding-bottom: 1 rem;
}
</style>
